import { BLACK, WHITE } from '@/composables/gameConstants.js'

export const GAME_RECORD_VERSION = 1
export const GAME_RECORD_KIND = 'othello-reversi'

function formatFilenameTime(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`
}

function sanitizeFilenamePart(s, fallback = 'player') {
  const v = String(s ?? '')
    .trim()
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, PLAYER_NAME_MAX)
  return v || fallback
}

/**
 * 默认下载文件名：本地时间 YYYY-MM-DD-HH-mm-ss.json
 */
export function defaultJsonFilename() {
  return `${formatFilenameTime()}.json`
}

export function buildGameRecordFilename(blackName, whiteName) {
  const b = sanitizeFilenamePart(blackName, 'black')
  const w = sanitizeFilenamePart(whiteName, 'white')
  return `${b}-vs-${w}-${formatFilenameTime()}.json`
}

const PLAYER_NAME_MAX = 24

function clipName(s) {
  return String(s ?? '')
    .trim()
    .slice(0, PLAYER_NAME_MAX)
}

/**
 * @param {{ gameMode: string, gameOver: boolean, winner: string | null, moveLog: unknown[], playerNameBlack?: string, playerNameWhite?: string }} state
 */
export function serializeGameRecord(state) {
  const payload = {
    version: GAME_RECORD_VERSION,
    game: GAME_RECORD_KIND,
    exportedAt: new Date().toISOString(),
    gameMode: state.gameMode,
    savedGameOver: state.gameOver,
    winner: state.winner,
    moveLog: JSON.parse(JSON.stringify(state.moveLog)),
    playerNameBlack: clipName(state.playerNameBlack),
    playerNameWhite: clipName(state.playerNameWhite)
  }
  return JSON.stringify(payload, null, 2)
}

function isValidPlayer(p) {
  return p === BLACK || p === WHITE
}

/**
 * @param {string} text
 * @returns {{ gameMode: 'pvp'|'pvc', savedGameOver: boolean, winner: string | null, moveLog: Array<{ serial: number, player: number, index: number, square: string, flipCount: number }>, playerNameBlack: string, playerNameWhite: string }}
 */
export function parseGameRecord(text) {
  let data
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error('JSON')
  }
  if (!data || typeof data !== 'object') throw new Error('ROOT')
  if (data.version !== GAME_RECORD_VERSION) throw new Error('VERSION')
  if (!Array.isArray(data.moveLog)) throw new Error('MOVELOG')
  const mode = data.gameMode
  if (mode !== 'pvp' && mode !== 'pvc') throw new Error('GAMEMODE')

  const log = []
  for (const m of data.moveLog) {
    if (!m || typeof m !== 'object') throw new Error('MOVE')
    const { serial, player, index } = m
    if (typeof serial !== 'number' || serial < 1) throw new Error('SERIAL')
    if (!isValidPlayer(player)) throw new Error('PLAYER')
    if (typeof index !== 'number' || index < 0 || index > 63) throw new Error('INDEX')
    const flipCount = typeof m.flipCount === 'number' ? m.flipCount : 0
    log.push({
      serial,
      player,
      index,
      square: typeof m.square === 'string' ? m.square : '',
      flipCount
    })
  }

  const savedGameOver = Boolean(data.savedGameOver ?? data.gameOver)
  const winner =
    data.winner === 'black' || data.winner === 'white' || data.winner === 'draw'
      ? data.winner
      : savedGameOver
        ? null
        : null

  const playerNameBlack =
    typeof data.playerNameBlack === 'string'
      ? clipName(data.playerNameBlack)
      : ''
  const playerNameWhite =
    typeof data.playerNameWhite === 'string'
      ? clipName(data.playerNameWhite)
      : ''

  return {
    gameMode: mode,
    savedGameOver,
    winner,
    moveLog: log,
    playerNameBlack,
    playerNameWhite
  }
}

export function downloadTextFile(filename, text, mime = 'application/json;charset=utf-8') {
  const blob = new Blob([text], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
